/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateCurrencyRequest } from '../models/create-currency-request';
import { CurrencyResponse } from '../models/currency-response';
import { GenericResponse } from '../models/generic-response';
import { GetCurrencyResponse } from '../models/get-currency-response';
import { UpdateCurrencyRequest } from '../models/update-currency-request';

/**
 * Currency APIs
 */
@Injectable({ providedIn: 'root' })
export class CurrencyService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllCurrencies()` */
  static readonly GetAllCurrenciesPath = '/api/v1/currencies';

  /**
   * Get list of currencies.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllCurrencies()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllCurrencies$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - name
       * - code
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetCurrencyResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CurrencyService.GetAllCurrenciesPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetCurrencyResponse>;
        })
      );
  }

  /**
   * Get list of currencies.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllCurrencies$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllCurrencies(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - name
       * - code
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetCurrencyResponse> {
    return this.getAllCurrencies$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetCurrencyResponse>): GetCurrencyResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createCurrency()` */
  static readonly CreateCurrencyPath = '/api/v1/currencies';

  /**
   * create currency.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createCurrency()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCurrency$Response(
    params: {
      body: CreateCurrencyRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CurrencyResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CurrencyService.CreateCurrencyPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CurrencyResponse>;
        })
      );
  }

  /**
   * create currency.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createCurrency$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCurrency(
    params: {
      body: CreateCurrencyRequest;
    },
    context?: HttpContext
  ): Observable<CurrencyResponse> {
    return this.createCurrency$Response(params, context).pipe(
      map((r: StrictHttpResponse<CurrencyResponse>): CurrencyResponse => r.body)
    );
  }

  /** Path part for operation `getCurrencyById()` */
  static readonly GetCurrencyByIdPath = '/api/v1/currencies/{id}';

  /**
   * fetch Currency details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCurrencyById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCurrencyById$Response(
    params: {
      /**
       * The currencies id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CurrencyResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CurrencyService.GetCurrencyByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CurrencyResponse>;
        })
      );
  }

  /**
   * fetch Currency details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCurrencyById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCurrencyById(
    params: {
      /**
       * The currencies id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<CurrencyResponse> {
    return this.getCurrencyById$Response(params, context).pipe(
      map((r: StrictHttpResponse<CurrencyResponse>): CurrencyResponse => r.body)
    );
  }

  /** Path part for operation `deleteCurrencyById()` */
  static readonly DeleteCurrencyByIdPath = '/api/v1/currencies/{id}';

  /**
   * Delete Currency  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCurrencyById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCurrencyById$Response(
    params: {
      /**
       * The currency id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CurrencyService.DeleteCurrencyByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Currency  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteCurrencyById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCurrencyById(
    params: {
      /**
       * The currency id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteCurrencyById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateCurrency()` */
  static readonly UpdateCurrencyPath = '/api/v1/currencies/{id}';

  /**
   * update currency.
   *
   * update Currency
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCurrency()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCurrency$Response(
    params: {
      /**
       * The currency id that needs to be updated
       */
      id: number;
      body: UpdateCurrencyRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CurrencyResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CurrencyService.UpdateCurrencyPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CurrencyResponse>;
        })
      );
  }

  /**
   * update currency.
   *
   * update Currency
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCurrency$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCurrency(
    params: {
      /**
       * The currency id that needs to be updated
       */
      id: number;
      body: UpdateCurrencyRequest;
    },
    context?: HttpContext
  ): Observable<CurrencyResponse> {
    return this.updateCurrency$Response(params, context).pipe(
      map((r: StrictHttpResponse<CurrencyResponse>): CurrencyResponse => r.body)
    );
  }
}
